import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Users, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { customerAPI } from '../services/api';
import SystemHealth from '../components/SystemHealth';

const STATUS_COLORS = {
  'Active': '#00a933',
  'active': '#00a933',
  'Pending Installation': '#3b82f6',
  'Pending Instalation': '#3b82f6',
  'In Process': '#f59e0b',
  'Standby': '#8b5cf6',
  'Canceled': '#ef4444',
  'Cancelled': '#ef4444',
  'Future Fiber': '#06b6d4',
  'Not Processed': '#6b7280',
  'Not Procesed': '#6b7280',
  'Reminder': '#ec4899',
  'Suspended': '#dc2626',
  'Temporary Disconnected': '#f97316',
  'Temporarily Disconnected': '#f97316',
  'Temporarily disconnected': '#f97316',
  'temporary disconnected': '#f97316',
  'Done': '#00a933',
  'done': '#00a933'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery('statistics', () =>
    customerAPI.getStatistics().then(res => res.data.data),
    {
      staleTime: 3 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  // Memoize data transformations
  const statusData = useMemo(() =>
    stats?.byStatus?.map(item => ({
      name: item.status,
      value: parseInt(item.count),
      color: STATUS_COLORS[item.status] || '#6b7280'
    })) || [],
    [stats?.byStatus]
  );

  const quickStats = useMemo(() => [
    {
      name: 'Total Customers',
      value: stats?.total || 0,
      icon: Users,
      color: 'bg-blue-500',
      filter: '' // All customers
    },
    {
      name: 'Active',
      value: statusData
        .filter(s => s.name === 'Active' || s.name === 'Done')
        .reduce((sum, s) => sum + s.value, 0),
      icon: CheckCircle,
      color: 'bg-green-500',
      filter: 'Active'
    },
    {
      name: 'Pending',
      value: statusData
        .filter(s => s.name === 'Pending Installation' || s.name === 'Pending Instalation')
        .reduce((sum, s) => sum + s.value, 0),
      icon: Clock,
      color: 'bg-yellow-500',
      filter: 'Pending Installation'
    },
    {
      name: 'In Process',
      value: statusData
        .filter(s => s.name === 'In Process')
        .reduce((sum, s) => sum + s.value, 0),
      icon: TrendingUp,
      color: 'bg-purple-500',
      filter: 'In Process'
    }
  ], [stats?.total, statusData]);

  // Quick filter cards for status navigation with counts
  const statusCards = useMemo(() => [
    {
      name: 'Temp. Disconnected',
      status: 'Temporary Disconnected',
      iconColor: 'bg-orange-500',
      count: statusData.filter(s => s.name === 'Temporary Disconnected' || s.name === 'Temporarily Disconnected' || s.name === 'Temporarily disconnected').reduce((sum, s) => sum + s.value, 0)
    },
    {
      name: 'Not Processed',
      status: 'Not Processed',
      iconColor: 'bg-gray-500',
      count: statusData.filter(s => s.name === 'Not Processed' || s.name === 'Not Procesed').reduce((sum, s) => sum + s.value, 0)
    },
    {
      name: 'Standby',
      status: 'Standby',
      iconColor: 'bg-purple-500',
      count: statusData.filter(s => s.name === 'Standby').reduce((sum, s) => sum + s.value, 0)
    },
    {
      name: 'Cancelled',
      status: 'Canceled',
      iconColor: 'bg-red-500',
      count: statusData.filter(s => s.name === 'Canceled' || s.name === 'Cancelled').reduce((sum, s) => sum + s.value, 0)
    }
  ], [statusData]);

  const handleStatClick = (filter) => {
    if (filter) {
      navigate(`/customers?status=${encodeURIComponent(filter)}`);
    } else {
      navigate('/customers');
    }
  };

  const handleStatusTabClick = (status) => {
    navigate(`/customers?status=${encodeURIComponent(status)}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1 text-sm md:text-base">Overview of fiber connections</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
              onClick={() => handleStatClick(stat.filter)}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-600 text-xs md:text-sm truncate">{stat.name}</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{stat.value}</p>
                </div>
                <div className={`p-2 md:p-3 rounded-full ${stat.color} flex-shrink-0 ml-2`}>
                  <Icon className="text-white" size={18} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {statusCards.map((card) => (
          <div
            key={card.status}
            className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
            onClick={() => handleStatusTabClick(card.status)}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-gray-600 text-xs md:text-sm truncate">{card.name}</p>
                <p className="text-xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{card.count}</p>
              </div>
              <div className={`w-3 h-3 md:w-4 md:h-4 rounded-full ${card.iconColor} flex-shrink-0 ml-2`} />
            </div>
          </div>
        ))}
      </div>

      {/* System Health */}
      <SystemHealth />

    </div>
  );
};

export default Dashboard;
